// Domain and resource definitions derived from Dawn routes.
// Each resource has read and write scopes following the pattern:
//   domain:resource:read / domain:resource:write
// Auth is special — scopes are granted or not, with no read/write axis.

type Resource = {
  label: string;
  key: string;
  description: string;
};

type Domain = {
  label: string;
  key: string;
  description: string;
  resources: Resource[];
};

type AuthScope = {
  label: string;
  scope: string;
  description: string;
};

const domains: Domain[] = [
  {
    label: 'Organisations',
    key: 'organisations',
    description: 'Access to organisation resources and configuration',
    resources: [
      {
        label: 'Inventories',
        key: 'inventories',
        description: 'Emission inventories and reporting data',
      },
      {
        label: 'Buildings',
        key: 'buildings',
        description: 'Building profiles including floor plans and metadata',
      },
      {
        label: 'Business units',
        key: 'business-units',
        description: 'Organisational structure and business unit hierarchy',
      },
      {
        label: 'Activity streams',
        key: 'activity-streams',
        description: 'Streams of activity data from connected sources',
      },
      {
        label: 'Activity sources',
        key: 'activity-sources',
        description: 'Configured data sources feeding activity streams',
      },
      {
        label: 'Activity stream attributions',
        key: 'activity-stream-attributions',
        description:
          'Rules allocating activity streams to buildings, products, and business units',
      },
      {
        label: 'Disclosures',
        key: 'disclosures',
        description:
          'Formal emissions and activity disclosures for audit and reporting',
      },
    ],
  },
  {
    label: 'Precincts',
    key: 'precincts',
    description: 'Access to precinct resources and spatial data',
    resources: [
      {
        label: 'Workspaces',
        key: 'workspaces',
        description: 'Precinct workspaces and their configurations',
      },
      {
        label: 'Apps',
        key: 'apps',
        description: 'Applications deployed within precincts',
      },
      {
        label: 'Geographies',
        key: 'geographies',
        description: 'Geographic boundaries and spatial datasets',
      },
    ],
  },
  {
    label: 'Communities',
    key: 'communities',
    description: 'Access to community resources and uploads',
    resources: [
      {
        label: 'Uploads',
        key: 'uploads',
        description: 'File uploads for community data',
      },
    ],
  },
];

const authScopes: AuthScope[] = [
  {
    label: 'Token',
    scope: 'auth:token',
    description: 'Exchange credentials for access tokens',
  },
  {
    label: 'User info',
    scope: 'auth:userinfo',
    description: 'Read the authenticated user profile',
  },
  {
    label: 'Identity token',
    scope: 'auth:identity-token',
    description: 'Obtain signed identity tokens',
  },
];

// Scope string helpers.

const domainWildcard = (domain: Domain): string => `${domain.key}:*`;
const domainReadWildcard = (domain: Domain): string => `${domain.key}:*:read`;
const resourceRead = (domain: Domain, resource: Resource): string =>
  `${domain.key}:${resource.key}:read`;
const resourceWrite = (domain: Domain, resource: Resource): string =>
  `${domain.key}:${resource.key}:write`;

// Scope matching — mirrors dawn-core/src/scope.rs logic.

const scopeMatches = (granted: string, required: string): boolean => {
  const grantedParts = granted.split(':');
  const requiredParts = required.split(':');

  for (let i = 0; i < grantedParts.length; i++) {
    const g = grantedParts[i];

    if (g === '*' && i === grantedParts.length - 1) {
      return true;
    }

    if (g === '*') {
      if (i >= requiredParts.length) return false;
      continue;
    }

    if (i >= requiredParts.length) return false;
    if (g !== requiredParts[i]) return false;
  }

  return grantedParts.length === requiredParts.length;
};

const scopesSatisfy = (granted: string[], required: string): boolean =>
  granted.some((g) => scopeMatches(g, required));

// Produce a human-readable summary from a raw scope string array
// (as stored on the server). Used for displaying existing keys.

const summariseScopes = (scopes: string[]): string => {
  if (scopes.length === 0) return 'No access';
  if (scopes.some((s) => s === '*')) return 'Full access';
  return `${scopes.length} scope${scopes.length === 1 ? '' : 's'}`;
};

export type { Domain, Resource, AuthScope };
export {
  domains,
  authScopes,
  domainWildcard,
  domainReadWildcard,
  resourceRead,
  resourceWrite,
  scopeMatches,
  scopesSatisfy,
  summariseScopes,
};
